import { useEffect, useState } from "react";
import moment from "moment";
import { Box, Button, MenuItem, Select, Skeleton } from "@mui/material";
import { DatePicker, DayOfWeek, Label, Toggle } from "@fluentui/react";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../authConfig";
import { getPeriodCourseRegistrations, getPeoplenetPeriodCourseRegistration } from "../api";
import { create4DRegistrationObject, createPeoplenetRegistrationObject, ICourseRegistration } from "../utilities/utils";
import {
    aggregateRevenueByMonth,
    aggregateRevenueByWeek,
    mergeRevenueByWeek,
    aggregateCountByMonth,
    topCoursesByRevenue,
    topCoursesByCount,
    aggregateSourceBreakdown,
    aggregateRevenueByCategory,
    bestMonths,
    aggregateByDayOfWeek,
    aggregateByHour,
    computeRepeatRate,
    computeKpis,
    compareKpi,
    mergeRevenueByMonth,
    mergeCountByMonth,
    mergeByDayOfWeek,
    mergeByHour,
    mergeBestMonths,
    mergeTopCoursesRevenue,
    mergeTopCoursesCount,
    mergeCategoryRevenue,
    mergeSourceBreakdown,
} from "../utilities/dashboardAggregations";
import { KpiCards } from "./dashboard/KpiCards";
import { RevenueChart } from "./dashboard/RevenueChart";
import { RegistrationsChart } from "./dashboard/RegistrationsChart";
import { TopCoursesRevenueChart } from "./dashboard/TopCoursesRevenueChart";
import { TopCoursesCountChart } from "./dashboard/TopCoursesCountChart";
import { SourcePieChart } from "./dashboard/SourcePieChart";
import { CategoryRevenueChart } from "./dashboard/CategoryRevenueChart";
import { BestMonthsChart } from "./dashboard/BestMonthsChart";
import { BestDaysChart } from "./dashboard/BestDaysChart";
import { BestHoursChart } from "./dashboard/BestHoursChart";
import { WeeklyRevenueTable } from "./dashboard/WeeklyRevenueTable";
import { WeeklyRevenueChart } from "./dashboard/WeeklyRevenueChart";
import classes from "../styles/DashboardView.module.css";

export function DashboardView() {
    const { instance, accounts } = useMsal();
    const [startDate, setStartDate] = useState<Date | null>(moment().isoWeek(1).startOf("isoWeek").toDate());
    const [endDate, setEndDate] = useState<Date | null>(new Date());
    const [selectedPeriod, setSelectedPeriod] = useState<string>("thisYear");
    const [selectedFirm, setSelectedFirm] = useState<string>("Alle");
    const [dateBasis, setDateBasis] = useState<"registration" | "course">("registration");
    const [includeInternal, setIncludeInternal] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [dataLoaded, setDataLoaded] = useState<boolean>(false);
    const [chartData, setChartData] = useState<any>(null);
    const [accessToken, setAccessToken] = useState<string | null>(null);

    useEffect(() => {
        if (!accessToken) {
            instance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0],
            }).then((response) => {
                if (response?.accessToken) {
                    setAccessToken(response.accessToken);
                }
            });
        }
    }, []);

    const handlePeriodChange = (period: string) => {
        setSelectedPeriod(period);
        const now = new Date();
        const y = now.getFullYear();
        if (period === "thisYear") {
            // ISO week-year: matches Tilmeldinger's Årsoversigt bucketing so totals agree.
            // Jan 1 2026 is a Thursday → ISO week 1 2026 starts Mon Dec 29 2025.
            setStartDate(moment().isoWeek(1).startOf("isoWeek").toDate());
            setEndDate(new Date());
        } else if (period === "lastYear") {
            setStartDate(moment().subtract(1, "year").isoWeek(1).startOf("isoWeek").toDate());
            setEndDate(moment().isoWeek(1).startOf("isoWeek").subtract(1, "millisecond").toDate());
        } else if (period === "q1") {
            setStartDate(new Date(y, 0, 1));
            setEndDate(new Date(y, 2, 31));
        } else if (period === "q2") {
            setStartDate(new Date(y, 3, 1));
            setEndDate(new Date(y, 5, 30));
        } else if (period === "q3") {
            setStartDate(new Date(y, 6, 1));
            setEndDate(new Date(y, 8, 30));
        } else if (period === "q4") {
            setStartDate(new Date(y, 9, 1));
            setEndDate(new Date(y, 11, 31));
        }
    };

    const INTERNAL_EMAIL_DOMAINS = ["@peoplenet.dk", "@4d.dk"];

    const isInternalRegistration = (reg: ICourseRegistration): boolean => {
        const email = reg.contactPersonEmail?.trim().toLowerCase();
        if (!email) return false;
        return INTERNAL_EMAIL_DOMAINS.some(domain => email.endsWith(domain));
    };

    const fetchRegistrations = async (token: string, start: Date, end: Date, firm: string): Promise<ICourseRegistration[]> => {
        // In "course" mode the target window is by course start date. A course starting
        // in the window can have been registered well before it, so widen the fetch on
        // the registration-date side and filter+remap client-side. 3 years back covers
        // practically all advance bookings.
        const fetchStart = dateBasis === "course"
            ? new Date(start.getFullYear() - 3, start.getMonth(), start.getDate())
            : start;
        const fetchEnd = end;

        const periodParam = {
            startDate: new Date(new Date(fetchStart).setHours(0, 0, 0, 0)).toISOString(),
            endDate: new Date(new Date(fetchEnd).setHours(23, 59, 59, 999)).toISOString(),
        };
        let data: ICourseRegistration[] = [];

        if (firm === "4D" || firm === "Alle") {
            const response: any = await getPeriodCourseRegistrations(token, periodParam);
            if (response?.data) {
                data = data.concat(create4DRegistrationObject(response.data));
            }
        }

        if (firm === "PeopleNet" || firm === "Alle") {
            const response: any = await getPeoplenetPeriodCourseRegistration(periodParam);
            if (response?.data) {
                data = data.concat(createPeoplenetRegistrationObject(response.data));
            }
        }

        if (!includeInternal) {
            data = data.filter(reg => !isInternalRegistration(reg));
        }

        if (dateBasis === "course") {
            const startMs = new Date(start).setHours(0, 0, 0, 0);
            const endMs = new Date(end).setHours(23, 59, 59, 999);
            data = data
                .filter(reg => {
                    const sd = reg.startDate instanceof Date ? reg.startDate : new Date(reg.startDate);
                    const t = sd?.getTime();
                    return !!t && !isNaN(t) && t >= startMs && t <= endMs;
                })
                .map(reg => {
                    const sd = reg.startDate instanceof Date ? reg.startDate : new Date(reg.startDate);
                    return { ...reg, month: sd.getMonth().toString() };
                });
        }

        return data;
    };

    const handleLoadData = async () => {
        if (!accessToken || !startDate || !endDate) return;

        try {
            setLoading(true);
            const firm = selectedFirm;
            const token = accessToken;

            const usingIsoYear = selectedPeriod === "thisYear" || selectedPeriod === "lastYear";
            let prevStart: Date;
            let prevEnd: Date;
            let prev2Start: Date;
            let prev2End: Date;
            if (usingIsoYear) {
                const currIsoYear = moment(startDate).isoWeekYear();
                const prevIsoYear = currIsoYear - 1;
                const prev2IsoYear = currIsoYear - 2;
                prevStart = moment().isoWeekYear(prevIsoYear).isoWeek(1).startOf("isoWeek").toDate();
                prevEnd = selectedPeriod === "lastYear"
                    ? moment().isoWeekYear(currIsoYear).isoWeek(1).startOf("isoWeek").subtract(1, "millisecond").toDate()
                    : moment(endDate).subtract(1, "year").toDate();
                prev2Start = moment().isoWeekYear(prev2IsoYear).isoWeek(1).startOf("isoWeek").toDate();
                prev2End = selectedPeriod === "lastYear"
                    ? moment().isoWeekYear(prevIsoYear).isoWeek(1).startOf("isoWeek").subtract(1, "millisecond").toDate()
                    : moment(endDate).subtract(2, "year").toDate();
            } else {
                prevStart = new Date(startDate);
                prevStart.setFullYear(prevStart.getFullYear() - 1);
                prevEnd = new Date(endDate);
                prevEnd.setFullYear(prevEnd.getFullYear() - 1);
                prev2Start = new Date(startDate);
                prev2Start.setFullYear(prev2Start.getFullYear() - 2);
                prev2End = new Date(endDate);
                prev2End.setFullYear(prev2End.getFullYear() - 2);
            }

            const [data, prevData, prev2Data] = await Promise.all([
                fetchRegistrations(token, startDate, endDate, firm),
                fetchRegistrations(token, prevStart, prevEnd, firm),
                fetchRegistrations(token, prev2Start, prev2End, firm),
            ]);

            const kpis = computeKpis(data, firm);
            const prevKpis = computeKpis(prevData, firm);
            const repeat = computeRepeatRate(data);
            const prevRepeat = computeRepeatRate(prevData);

            const comparison = {
                totalRevenue: compareKpi(kpis.totalRevenue, prevKpis.totalRevenue),
                totalRegistrations: compareKpi(kpis.totalRegistrations, prevKpis.totalRegistrations),
                avgRevenue: compareKpi(kpis.avgRevenue, prevKpis.avgRevenue),
                avgParticipants: compareKpi(kpis.avgParticipants, prevKpis.avgParticipants),
                repeatRate: compareKpi(repeat.repeatRate, prevRepeat.repeatRate),
            };

            const revenueByMonth = aggregateRevenueByMonth(data, firm);
            const prevRevenueByMonth = aggregateRevenueByMonth(prevData, firm);
            const revenueByWeek = aggregateRevenueByWeek(data, firm, startDate, endDate, dateBasis === "course");
            const prevRevenueByWeek = aggregateRevenueByWeek(prevData, firm, prevStart, prevEnd, dateBasis === "course");
            const prev2RevenueByWeek = aggregateRevenueByWeek(prev2Data, firm, prev2Start, prev2End, dateBasis === "course");
            const countByMonth = aggregateCountByMonth(data);
            const prevCountByMonth = aggregateCountByMonth(prevData);
            const topRevenue = topCoursesByRevenue(data, firm);
            const prevTopRevenue = topCoursesByRevenue(prevData, firm, 9999);
            const topCount = topCoursesByCount(data);
            const prevTopCount = topCoursesByCount(prevData, 9999);
            const sources = aggregateSourceBreakdown(data, firm);
            const prevSources = aggregateSourceBreakdown(prevData, firm);
            const categoryRevenue = aggregateRevenueByCategory(data, firm);
            const prevCategoryRevenue = aggregateRevenueByCategory(prevData, firm);
            const bestMonthsData = bestMonths(data, firm);
            const prevBestMonthsData = bestMonths(prevData, firm);
            const dayData = aggregateByDayOfWeek(data);
            const prevDayData = aggregateByDayOfWeek(prevData);
            const hourData = aggregateByHour(data);
            const prevHourData = aggregateByHour(prevData);

            setChartData({
                kpis,
                prevKpis,
                repeat,
                prevRepeat,
                comparison,
                prevYearLabel: usingIsoYear
                    ? (moment(startDate).isoWeekYear() - 1).toString()
                    : prevStart.getFullYear().toString(),
                prev2YearLabel: usingIsoYear
                    ? (moment(startDate).isoWeekYear() - 2).toString()
                    : prev2Start.getFullYear().toString(),
                currentYearLabel: usingIsoYear
                    ? moment(startDate).isoWeekYear().toString()
                    : startDate.getFullYear().toString(),
                revenueByMonth: mergeRevenueByMonth(revenueByMonth, prevRevenueByMonth),
                revenueByWeek: mergeRevenueByWeek(revenueByWeek, prevRevenueByWeek, prev2RevenueByWeek),
                dateBasisLabel: dateBasis === "course" ? "Efter kursus startdato" : "Efter tilmeldingsdato",
                countByMonth: mergeCountByMonth(countByMonth, prevCountByMonth),
                topRevenue: mergeTopCoursesRevenue(topRevenue, prevTopRevenue),
                topCount: mergeTopCoursesCount(topCount, prevTopCount),
                sources: mergeSourceBreakdown(sources, prevSources),
                categoryRevenue: mergeCategoryRevenue(categoryRevenue, prevCategoryRevenue),
                bestMonthsData: mergeBestMonths(bestMonthsData, prevBestMonthsData),
                dayData: mergeByDayOfWeek(dayData, prevDayData),
                hourData: mergeByHour(hourData, prevHourData),
            });
            setDataLoaded(true);
        } catch (e) {
            console.log(e);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <div className={classes.filters}>
                <div>
                    <Label>Periode</Label>
                    <Select
                        size="small"
                        value={selectedPeriod}
                        onChange={(e) => handlePeriodChange(e.target.value)}
                        className={classes.filter}
                    >
                        <MenuItem value="thisYear">Indeværende år</MenuItem>
                        <MenuItem value="lastYear">Sidste år</MenuItem>
                        <MenuItem value="q1">Q1 (jan–mar)</MenuItem>
                        <MenuItem value="q2">Q2 (apr–jun)</MenuItem>
                        <MenuItem value="q3">Q3 (jul–sep)</MenuItem>
                        <MenuItem value="q4">Q4 (okt–dec)</MenuItem>
                        <MenuItem value="custom">Brugerdefineret</MenuItem>
                    </Select>
                </div>
                {selectedPeriod === "custom" && (
                    <>
                        <div>
                            <Label>Fra dato</Label>
                            <DatePicker
                                className={classes.filter}
                                firstDayOfWeek={DayOfWeek.Monday}
                                value={startDate}
                                onSelectDate={(date) => setStartDate(date)}
                                formatDate={(date) => date?.toLocaleDateString("da-DK") ?? ""}
                            />
                        </div>
                        <div>
                            <Label>Til dato</Label>
                            <DatePicker
                                className={classes.filter}
                                firstDayOfWeek={DayOfWeek.Monday}
                                value={endDate}
                                onSelectDate={(date) => setEndDate(date)}
                                formatDate={(date) => date?.toLocaleDateString("da-DK") ?? ""}
                            />
                        </div>
                    </>
                )}
                <div>
                    <Label>Firma</Label>
                    <Select
                        size="small"
                        value={selectedFirm}
                        onChange={(e) => setSelectedFirm(e.target.value)}
                        className={classes.filter}
                    >
                        <MenuItem value="Alle">Vis alle</MenuItem>
                        <MenuItem value="4D">4D</MenuItem>
                        <MenuItem value="PeopleNet">PeopleNet</MenuItem>
                    </Select>
                </div>
                <div>
                    <Label>Opgør efter</Label>
                    <Select
                        size="small"
                        value={dateBasis}
                        onChange={(e) => setDateBasis(e.target.value === "course" ? "course" : "registration")}
                        className={classes.filter}
                    >
                        <MenuItem value="registration">Tilmeldingsdato</MenuItem>
                        <MenuItem value="course">Kursus startdato</MenuItem>
                    </Select>
                </div>
                <div className={classes.toggleGroup}>
                    <Toggle
                        label="Ekskluder interne tilmeldinger"
                        checked={!includeInternal}
                        onText="Ja"
                        offText="Nej"
                        onChange={(_, checked) => setIncludeInternal(!checked)}
                    />
                </div>
                <Button
                    variant="contained"
                    startIcon={<AssessmentIcon />}
                    onClick={handleLoadData}
                    disabled={loading}
                    className={classes.submitBtn}
                >
                    {loading ? "Henter..." : "Vis statistik"}
                </Button>
            </div>

            {loading && (
                <Box>
                    <Skeleton variant="rectangular" height={100} sx={{ mb: 3 }} />
                    <Skeleton variant="rectangular" height={300} sx={{ mb: 3 }} />
                </Box>
            )}

            {dataLoaded && !loading && chartData && (
                <>
                    <KpiCards
                        totalRevenue={chartData.kpis.totalRevenue}
                        totalRegistrations={chartData.kpis.totalRegistrations}
                        avgRevenue={chartData.kpis.avgRevenue}
                        avgParticipants={chartData.kpis.avgParticipants}
                        topCourse={chartData.kpis.topCourse}
                        topCourseCount={chartData.kpis.topCourseCount}
                        repeatRate={chartData.repeat.repeatRate}
                        prevTopCourse={chartData.prevKpis.topCourse}
                        prevTopCourseCount={chartData.prevKpis.topCourseCount}
                        comparison={chartData.comparison}
                        prevYearLabel={chartData.prevYearLabel}
                    />

                    <div className={classes.chartsGrid}>
                        <div className={classes.fullWidth}>
                            <RevenueChart data={chartData.revenueByMonth} currentYearLabel={chartData.currentYearLabel} prevYearLabel={chartData.prevYearLabel} />
                        </div>
                        <div className={classes.fullWidth}>
                            <WeeklyRevenueChart
                                data={chartData.revenueByWeek}
                                basisLabel={chartData.dateBasisLabel}
                                currentYearLabel={chartData.currentYearLabel}
                                prevYearLabel={chartData.prevYearLabel}
                                prev2YearLabel={chartData.prev2YearLabel}
                            />
                        </div>
                        <div className={classes.fullWidth}>
                            <WeeklyRevenueTable
                                data={chartData.revenueByWeek}
                                basisLabel={chartData.dateBasisLabel}
                                currentYearLabel={chartData.currentYearLabel}
                                prevYearLabel={chartData.prevYearLabel}
                            />
                        </div>
                        <div className={classes.fullWidth}>
                            <RegistrationsChart data={chartData.countByMonth} currentYearLabel={chartData.currentYearLabel} prevYearLabel={chartData.prevYearLabel} />
                        </div>
                        <TopCoursesRevenueChart data={chartData.topRevenue} currentYearLabel={chartData.currentYearLabel} prevYearLabel={chartData.prevYearLabel} />
                        <TopCoursesCountChart data={chartData.topCount} currentYearLabel={chartData.currentYearLabel} prevYearLabel={chartData.prevYearLabel} />
                        <div className={classes.fullWidth}>
                            <CategoryRevenueChart data={chartData.categoryRevenue} currentYearLabel={chartData.currentYearLabel} prevYearLabel={chartData.prevYearLabel} />
                        </div>
                        <div className={classes.fullWidth}>
                            <BestMonthsChart data={chartData.bestMonthsData} currentYearLabel={chartData.currentYearLabel} prevYearLabel={chartData.prevYearLabel} />
                        </div>
                        <BestDaysChart data={chartData.dayData} currentYearLabel={chartData.currentYearLabel} prevYearLabel={chartData.prevYearLabel} />
                        <BestHoursChart data={chartData.hourData} currentYearLabel={chartData.currentYearLabel} prevYearLabel={chartData.prevYearLabel} />
                        <div className={classes.fullWidth}>
                            <SourcePieChart data={chartData.sources} currentYearLabel={chartData.currentYearLabel} prevYearLabel={chartData.prevYearLabel} />
                        </div>
                    </div>
                </>
            )}

            {!dataLoaded && !loading && (
                <Box sx={{ textAlign: "center", color: "#999", mt: 8 }}>
                    <AssessmentIcon sx={{ fontSize: 64, mb: 2, color: "#ccc" }} />
                    <p>Vælg en periode og tryk "Vis statistik" for at se data</p>
                </Box>
            )}
        </Box>
    );
}
